import React, { RefObject, useCallback, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useResourceStatsFiles } from 'new/hooks/adapters/useResourceStatsFiles';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import ReadStatusHeader from 'new/elements/ReadStats/components/ReadStatusHeader';
import { useFilteredData } from 'new/hooks/useFilteredData';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Stack } from 'new/elements/Stack';
import { Table } from 'new/elements/Table';
import { Pagination } from 'new/elements/Pagination';
import { FileItemListRow, IngestionStatusInfo } from 'new/elements/FileItemsList/types';
import DateUtils from 'js/lib/DateUtils';
import FormatUtils from 'js/lib/FormatUtils';
import TruncatedText from 'js/components/elements/TruncatedText';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import styles from '../ReadStats.module.scss';

interface TaskItemsProps {
  resourceType: string;
  resourceId: string;
  mode?: keyof typeof FILE_LIST_MODE_TYPES;
  connectionCategory: string;
  parentScrollRef?: RefObject<any>;
  height?: number;
  data?: any;
  requestFiles: (page: number) => void;
  onChangeSort: (e: any) => void;
  onChangeStatus: (e: any) => void;
  sort: string;
  status: string;
  sortByItems: any[];
  filterByStatus: any[];
}

function getColumns(
  resourceType: string,
  mode: keyof typeof FILE_LIST_MODE_TYPES,
  hideRecords: boolean = false,
) {
  const isDataSinkType = resourceType === 'data_sinks';
  const isReadMode = mode === FILE_LIST_MODE_TYPES.MODE_READ;
  const isQuarantineMode = mode === FILE_LIST_MODE_TYPES.MODE_QUARANTINE_FILES;

  return [
    {
      name: 'Details',
      field: 'metadata.display_path',
      width: 30,
      renderRow: (row: FileItemListRow) => {
        if (!row) return null;
        const displayName = row.metadata?.display_path || row.name;
        let lastChange = row?.lastIngested;
        if (isReadMode) {
          lastChange = row?.lastIngested;
        } else if (isDataSinkType && !isQuarantineMode) {
          lastChange = row?.lastWritten;
        }
        const lastChangeFormatted = DateUtils.getDateString(lastChange) ?? '';
        const sizeFormatted = FormatUtils.bytesToString(row.size) ?? '';
        return (
          <Stack flex={1} paddingLeft="8px">
            <TruncatedText text={displayName} lines={1} />
            <Typography variant="body3">
              {[lastChangeFormatted, sizeFormatted].filter(Boolean).join(' - ')}
            </Typography>
          </Stack>
        );
      },
    },
    {
      name: 'Size',
      sortable: true,
      field: 'size',
      renderRow: ({ size }: FileItemListRow) => FormatUtils.bytesToString(size),
    },
    ...(hideRecords
      ? []
      : [
          {
            name: 'Records Count',
            field: 'recordCount',
            renderRow: ({ recordCount }: FileItemListRow) => FormatUtils.formatNumber(recordCount),
          },
        ]),
    {
      name: 'Error Count',
      field: 'errorCount',
      renderRow: ({ errorCount }: FileItemListRow) => FormatUtils.formatNumber(errorCount),
    },
    {
      name: 'Ingestion Status',
      sortable: true,
      field: 'ingestionStatus',
      renderRow(row: FileItemListRow) {
        const info =
          IngestionStatusInfo[
            mode === FILE_LIST_MODE_TYPES.MODE_READ ? row.ingestionStatus : row.writeStatus
          ] || IngestionStatusInfo.IN_PROGRESS;
        return (
          <ChipBadge label={info.title} color={info.color as any} className={styles.chipBadge} />
        );
      },
    },
  ];
}

export const TaskItems = (props: TaskItemsProps) => {
  const {
    resourceId,
    resourceType,
    mode,
    parentScrollRef,
    data,
    requestFiles,
    onChangeSort,
    onChangeStatus,
    sort,
    status,
    sortByItems,
    filterByStatus,
  } = props;

  // check if we hide records for this flow type
  const hideRecords = shouldHideRecordsForResource(data);

  const { drawerContentRef } = useDrawerContext();
  const { items, meta, loading } = useResourceStatsFiles(resourceType, resourceId);
  const pageCount = meta.pageCount || 1;
  const currentPage = meta.currentPage || 1;
  const { filteredData, onSearchChange } = useFilteredData(items, 'metadata.display_path');

  const loadMore = useCallback((page: number) => requestFiles(page), [requestFiles]);

  const onChangePage = (_e: any, page: number) => {
    loadMore(page);
  };

  const generatedColumns = useMemo(
    () => getColumns(resourceType, mode, hideRecords),
    [resourceType, mode, hideRecords],
  );

  return (
    <div aria-label="task-items">
      <ReadStatusHeader
        sort={sort}
        status={status}
        mode={mode}
        sortByItems={sortByItems}
        filterByStatus={filterByStatus}
        isAdaptiveFlow={true}
        onChangeSort={onChangeSort}
        onChangeStatus={onChangeStatus}
        onSearchChange={onSearchChange}
      />
      <Stack>
        <Table
          key={filteredData?.length}
          disableVirtualization
          headClassName={styles.headClassName}
          columns={generatedColumns}
          rows={filteredData}
          loading={loading}
          parentScrollRef={parentScrollRef || drawerContentRef}
          getRowTdClassName={() => styles.rowTdClassName}
          emptyStateText="No tasks found"
          accessoryData={{
            filters: { orderby: sort, status: status === 'all' ? '' : status },
          }}
        />
        {!pageCount || pageCount === 1 ? null : (
          <Stack className={styles.pagination}>
            <Pagination count={pageCount} page={currentPage} onChange={onChangePage} />
          </Stack>
        )}
      </Stack>
    </div>
  );
};
